import TheHangIcon from '@/assets/images/icons/thehang-icon.svg';
import { Tooltip } from '@/components/ui/Tooltip';
import { Ionicons } from '@expo/vector-icons';
import React from 'react';
import { LOADING_SPINNER_COLOR } from '@/lib/loadingSpinnerColor';
import { ActivityIndicator, Platform, TouchableOpacity, View } from 'react-native';
import { XStack, YStack } from 'tamagui';
import { CardTitle } from '@/components/ui/Typography';
import type { LessonMaterial } from '../../types';

export const renderMaterialIcon = (type: string) => {
  const commonColor = '#1B1B1B';
  const commonSize = 24;
  const iconStyle = { width: 28, height: 28, alignItems: 'center' as const, justifyContent: 'center' as const };

  const getIcon = () => {
    switch (type) {
      case 'pdf':
        return <Ionicons name="document-text-outline" size={commonSize} color={commonColor} />;
      case 'youtube_video':
        return <Ionicons name="logo-youtube" size={commonSize} color={commonColor} />;
      case 'course':
        return <Ionicons name="school-outline" size={commonSize} color={commonColor} />;
      case 'circle':
        return <TheHangIcon width={commonSize} height={commonSize} color={commonColor} />;
      case 'youtube_playlist':
        return <Ionicons name="albums-outline" size={commonSize} color={commonColor} />;
      case 'spotify_playlist':
        return <Ionicons name="musical-notes-outline" size={commonSize} color={commonColor} />;
      case 'mp3':
      case 'm4a':
      case 'wav':
        return <Ionicons name="musical-notes-outline" size={commonSize} color={commonColor} />;
      case 'mp4':
        return <Ionicons name="play-circle-outline" size={commonSize} color={commonColor} />;
      case 'other':
        return <Ionicons name="link" size={commonSize} color={commonColor} />;
      default:
        return <Ionicons name="link-outline" size={commonSize} color={commonColor} />;
    }
  };

  return <View style={iconStyle}>{getIcon()}</View>;
};

interface MaterialCardProps {
  children: React.ReactNode;
  clickable?: boolean;
}

export const MaterialCard = ({ children, clickable = false }: MaterialCardProps) => (
  <YStack
    p="$3"
    br="$4"
    backgroundColor="#F8F7F4"
    {...(Platform.OS === 'web' && {
      style: {
        transition: 'background-color 0.15s ease',
        ...(clickable && { cursor: 'pointer' }),
      },
      hoverStyle: { backgroundColor: '#F0EEE9' },
    })}
  >
    {children}
  </YStack>
);

interface PdfMaterialItemProps {
  material: LessonMaterial;
  downloadingPdfId: number | null;
  onView: (url: string, material: LessonMaterial) => void;
  onDownload: (url: string, material: LessonMaterial) => void;
}

export const PdfMaterialItem = React.memo(({ material, downloadingPdfId, onView, onDownload }: PdfMaterialItemProps) => (
  <MaterialCard>
    <XStack ai="center" gap="$3" minHeight={28}>
      {renderMaterialIcon(material.type)}
      <TouchableOpacity
        onPress={() => onView(material.url, material)}
        style={{ flex: 1, ...(Platform.OS === 'web' && { cursor: 'pointer' }) } as any}
      >
        <CardTitle fontWeight="600" color="#0D0D0D" numberOfLines={2}>
          {material.title.trim() || material.url}
        </CardTitle>
      </TouchableOpacity>
      <XStack ai="center" gap="$1">
        <Tooltip text="View">
          <TouchableOpacity
            onPress={() => onView(material.url, material)}
            style={{
              width: 36,
              height: 36,
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 8,
              ...(Platform.OS === 'web' && { cursor: 'pointer' }),
            } as any}
          >
            <Ionicons name="eye-outline" size={22} color="#666" />
          </TouchableOpacity>
        </Tooltip>
        <Tooltip text={downloadingPdfId === material.id ? 'Downloading...' : 'Download'}>
          <TouchableOpacity
            onPress={() => onDownload(material.url, material)}
            disabled={downloadingPdfId === material.id}
            style={{
              width: 36,
              height: 36,
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 8,
              opacity: downloadingPdfId === material.id ? 0.5 : 1,
              ...(Platform.OS === 'web' && { cursor: downloadingPdfId === material.id ? 'default' : 'pointer' }),
            } as any}
          >
            {downloadingPdfId === material.id ? (
              <ActivityIndicator size="small" color={LOADING_SPINNER_COLOR} />
            ) : (
              <Ionicons name="download-outline" size={22} color="#666" />
            )}
          </TouchableOpacity>
        </Tooltip>
      </XStack>
    </XStack>
  </MaterialCard>
));
PdfMaterialItem.displayName = 'PdfMaterialItem';

interface LinkMaterialItemProps {
  material: LessonMaterial;
  onPress: (material: LessonMaterial) => void;
}

export const LinkMaterialItem = React.memo(({ material, onPress }: LinkMaterialItemProps) => (
  <TouchableOpacity onPress={() => onPress(material)} activeOpacity={0.7}>
    <MaterialCard clickable>
      <XStack ai="center" gap="$3" minHeight={28}>
        {renderMaterialIcon(material.type)}
        <CardTitle fontWeight="600" color="#0D0D0D" flex={1} numberOfLines={2}>
          {material.title.trim() || material.url}
        </CardTitle>
        <Tooltip text="Open link">
          <View style={{ width: 28, height: 28, alignItems: 'center', justifyContent: 'center' }}>
            <Ionicons name="open-outline" size={20} color="#666" />
          </View>
        </Tooltip>
      </XStack>
    </MaterialCard>
  </TouchableOpacity>
));
LinkMaterialItem.displayName = 'LinkMaterialItem';
